import { observer } from "mobx-react-lite";
import { useEffect, type ReactNode } from "react";
import { Button } from "../../ui/button";
import { ErrorState } from "../../ui/error-state";
import { LoadingState } from "../../ui/loading-state";
import { useAppStores } from "../app-context";
import { EngageLogo } from "../engage-logo";
import { LoginView } from "./login-view";

export interface SessionGateProps {
  readonly children: ReactNode;
}

/**
 * Session boundary for the whole application.
 *
 * On mount it runs the one-shot `GET /auth/sessions/current` bootstrap, then
 * renders one of four states:
 *   loading  → the session outcome is still unknown
 *   error    → the session service could not be reached (retryable)
 *   login    → the caller is anonymous (401)
 *   children → the caller is authenticated; the routed app mounts
 *
 * The whole flow is driven off the injected stores, so tests that seed an
 * authenticated `SessionStore` render `children` directly without any network.
 */
export const SessionGate = observer(function SessionGate({ children }: SessionGateProps): ReactNode {
  const { api, locale, session } = useAppStores();

  useEffect(() => {
    if (session.status !== "authenticated" && session.bootstrapStatus === "idle") {
      void session.bootstrap(api);
    }
  }, [api, session]);

  if (session.bootstrapStatus === "idle" || session.bootstrapStatus === "loading") {
    return (
      <div className="session-boundary">
        <div aria-label={locale.text("appName")} role="img"><EngageLogo name={locale.text("appName")} subtitle={locale.text("shellEngageSubtitle")} /></div>
        <LoadingState label={locale.text("sessionLoading")} />
      </div>
    );
  }

  if (session.bootstrapStatus === "error") {
    return (
      <div className="session-boundary">
        <div aria-label={locale.text("appName")} role="img"><EngageLogo name={locale.text("appName")} subtitle={locale.text("shellEngageSubtitle")} /></div>
        <ErrorState
          action={
            <Button onClick={() => void session.bootstrap(api)} variant="primary">
              {locale.text("retry")}
            </Button>
          }
          description={locale.text("sessionErrorDetail")}
          title={locale.text("sessionErrorTitle")}
        />
      </div>
    );
  }

  // Tests and embedded hosts may inject a fully authenticated store. Do not
  // replace that state with an unsolicited network bootstrap. Check this only
  // after the bootstrap lifecycle above: `GET current` briefly authenticates
  // the identity before the default work context and its capability snapshot
  // have loaded. Mounting the routed shell in that interval produced a
  // half-authorized layout and stale navigation.
  if (session.status === "authenticated") return <>{children}</>;

  return <LoginView />;
});
